import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import hotToast from "react-hot-toast";
import api from "../../chat/api";
import { Spinner } from "./FolderPickerModal";
import { ThreeDotsIcon } from "./Icons";
import { sha256Hex } from "./kbHelpers";

const STATUS_TABS = [
  ["open", "Open"],
  ["closed", "Closed"],
  ["dismissed", "Dismissed"],
];
const CONTEXTS = [
  ["all", "All"],
  ["rfp_kb_copilot", "Knowledge Base Chat"],
  ["proposal_copilot", "Proposal Chat"],
];
const ACCEPT = ".pdf,.docx,.doc,.txt,.xlsx,.xls,.csv";
const GRID_OPEN = "grid grid-cols-[minmax(220px,1fr)_170px_120px_100px_180px] items-center";
const GRID_DONE = "grid grid-cols-[minmax(220px,1fr)_170px_220px_110px] items-center";
const GRID_COMPACT = "grid grid-cols-[minmax(0,1fr)_auto] items-center";
const GRID_COMPACT_DONE = "grid grid-cols-[minmax(0,1fr)_minmax(0,170px)] items-center";
// Below this table width the middle columns fold under the question and actions move into the "..." menu.
const COMPACT_BELOW = 800;
const PILL = "inline-flex px-[10px] py-[2px] rounded-full border text-[12px] whitespace-nowrap";

const orgParams = () => {
  const orgId = Cookies.get("selected_org_id");
  return orgId ? { organization_id: orgId } : {};
};

const Modal = ({ title, children, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[9999] bg-[rgba(24,16,28,0.45)] flex items-center justify-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="w-[520px] max-w-[92vw] max-h-[80vh] bg-white rounded-xl p-6 shadow-[0_24px_48px_rgba(24,16,28,0.3)] flex flex-col gap-4 font-outfit"
      >
        <p className="m-0 text-[18px] font-semibold text-[#18181B]">{title}</p>
        {children}
      </div>
    </div>
  );
};

const DismissModal = ({ gap, onCancel, onDismiss }) => {
  const [reason, setReason] = useState("");
  return (
    <Modal title="Dismiss this gap?" onClose={onCancel}>
      <p className="m-0 text-[14px] leading-[1.5] text-[#3F3F46]">
        “{gap.question}” moves to Dismissed. Use this when the question doesn't need a document, for example small talk.
      </p>
      <textarea
        id="kb-gap-dismiss-reason"
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional)"
        aria-label="Reason (optional)"
        className="px-3 py-2 border border-[#A1A1AA] rounded-md text-[14px] outline-none resize-none focus:border-[#C95EBE] focus:shadow-[0_0_0_1px_#C95EBE]"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2 border border-[#A1A1AA] rounded-md bg-white hover:bg-[#F4F4F5] text-[14px] font-medium text-[#27272A]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onDismiss(reason)}
          className="px-5 py-2 border border-[#A04A97] rounded-md bg-[#A04A97] hover:bg-[#8E3F86] text-white text-[14px] font-medium"
        >
          Dismiss
        </button>
      </div>
    </Modal>
  );
};

// Questions chat couldn't answer, with ways to close each one: add a source or dismiss.
const KnowledgeGapsPage = ({ onBack, onCountChange, formatDate, refreshKey, onSourceUploaded, onOpenDocument }) => {
  const [tab, setTab] = useState("open");
  const [context, setContext] = useState("all");
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [dismissFor, setDismissFor] = useState(null);
  const latestRequest = useRef(0);
  const fileInput = useRef(null);
  const uploadFor = useRef(null);
  const tableRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const [menu, setMenu] = useState(null);

  // Measured before the first paint so narrow screens never flash the wide layout.
  useLayoutEffect(() => {
    const el = tableRef.current;
    if (!el) return undefined;
    setCompact(el.getBoundingClientRect().width < COMPACT_BELOW);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setCompact(entry.contentRect.width < COMPACT_BELOW));
    observer.observe(el);
    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  const openMenu = (event, gap) => {
    event.stopPropagation();
    if (menu?.gap.id === gap.id) return setMenu(null);
    const r = event.currentTarget.getBoundingClientRect();
    const up = r.bottom + 140 > window.innerHeight;
    return setMenu({ gap, right: window.innerWidth - r.right, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  };

  const load = useCallback(async () => {
    // A slower answer for an earlier filter must not replace the current one.
    const request = ++latestRequest.current;
    setError("");
    try {
      const params = new URLSearchParams({ ...orgParams(), status: tab });
      if (context !== "all") params.append("context", context);
      const res = await api.get(`/api/v1/rfp/knowledge-base/gaps/?${params.toString()}`);
      if (request !== latestRequest.current) return;
      setItems(res.data?.results || []);
      setCounts(res.data?.counts || null);
      onCountChange?.(res.data?.open_count ?? 0);
    } catch (err) {
      if (request !== latestRequest.current) return;
      setItems([]);
      setError(err?.response?.data?.error || "Couldn't load knowledge gaps.");
    }
  }, [tab, context, onCountChange]);

  useEffect(() => {
    setItems(null);
    load();
  }, [load, refreshKey]);

  // Returns null once linked, otherwise the error so the caller can explain it.
  const linkDocument = async (gap, docId) => {
    try {
      await api.post(`/api/v1/rfp/knowledge-base/gaps/${gap.id}/link/`, { ...orgParams(), document_id: docId });
      return null;
    } catch (err) {
      return { status: err?.response?.status, code: err?.response?.data?.code, message: err?.response?.data?.error };
    } finally {
      // Someone else may have handled it meanwhile, so always show the current state.
      await load();
    }
  };

  const dismiss = async (gap, reason) => {
    setDismissFor(null);
    setBusyId(gap.id);
    try {
      await api.post(`/api/v1/rfp/knowledge-base/gaps/${gap.id}/dismiss/`, { ...orgParams(), reason });
      hotToast.success("Gap dismissed");
      await load();
    } catch (err) {
      hotToast.error(err?.response?.data?.error || "Couldn't dismiss this gap.");
      load();
    } finally {
      setBusyId(null);
    }
  };

  const pickSource = (gap) => {
    uploadFor.current = gap;
    if (fileInput.current) {
      fileInput.current.value = "";
      fileInput.current.click();
    }
  };

  const onFilePicked = async (event) => {
    const file = event.target.files?.[0];
    const gap = uploadFor.current;
    uploadFor.current = null;
    if (!file || !gap) return;
    setBusyId(gap.id);
    const toastId = hotToast.loading(`Uploading “${file.name}”...`);
    try {
      // A file that's already in the Knowledge Base is linked rather than uploaded twice.
      const hash = await sha256Hex(file);
      const dup = await api
        .post("/api/v1/rfp/knowledge-base/documents/check-duplicates/", { hashes: [hash], organization_id: Cookies.get("selected_org_id") || null })
        .then((r) => r.data?.matches?.[hash])
        .catch(() => null);
      if (dup?.id) {
        const failed = await linkDocument(gap, dup.id);
        if (!failed) {
          hotToast(`“${dup.name}” is already in your Knowledge Base, so it was linked instead.`, { id: toastId });
        } else if (failed.code === "document_unusable") {
          hotToast.error(
            `“${dup.name}” is already in your Knowledge Base but failed to process, so it can't answer this. Delete it from All Files, then add it again.`,
            { id: toastId, duration: 8000 }
          );
        } else {
          hotToast.error(failed.message || "Couldn't link this file.", { id: toastId });
        }
        return;
      }
      const form = new FormData();
      form.append("files", file);
      const params = new URLSearchParams(orgParams());
      const res = await api.post(`/api/v1/rfp/knowledge-base/upload/${params.toString() ? `?${params}` : ""}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const result = res.data?.results?.[0];
      if (!result?.doc_id || result.status === "failed") {
        throw new Error(result?.error || result?.reason || "Upload failed");
      }
      onSourceUploaded?.(result.pdf_key);
      const failed = await linkDocument(gap, result.doc_id);
      if (!failed) {
        hotToast.success("Uploaded. Approve it with View & Ingest; the gap closes once it's ingested.", { id: toastId });
      } else if (failed.status === 409) {
        hotToast(`“${result.file}” was uploaded to your Knowledge Base, but this gap was already handled.`, { id: toastId });
      } else {
        hotToast.error(failed.message || "Uploaded, but couldn't link it to this gap.", { id: toastId });
      }
    } catch (err) {
      hotToast.error(err?.response?.data?.error || err?.message || "Couldn't upload this file.", { id: toastId });
    } finally {
      setBusyId(null);
    }
  };

  const tabCount = (key) => {
    if (!counts) return null;
    return key === "open" ? (counts.open || 0) + (counts.pending || 0) : counts[key] || 0;
  };

  const isOpenTab = tab === "open";
  const grid = compact ? (isOpenTab ? GRID_COMPACT : GRID_COMPACT_DONE) : isOpenTab ? GRID_OPEN : GRID_DONE;
  const pad = compact ? "px-2" : "px-3";
  const menuItems = (gap) => [
    ...(gap.status === "open" && compact ? [["Add a source", () => pickSource(gap)]] : []),
    ["Dismiss", () => setDismissFor(gap)],
  ];

  return (
    <div className="flex-1 min-h-0 flex flex-col font-outfit">
      <input ref={fileInput} type="file" accept={ACCEPT} className="hidden" onChange={onFilePicked} />
      {menu && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: menu.top, bottom: menu.bottom, right: menu.right }}
          className="z-[1000] w-[200px] p-1 bg-white border border-[#D4D4D8] rounded-lg shadow-[0_8px_24px_rgba(24,16,28,0.16)] flex flex-col font-outfit"
        >
          {menuItems(menu.gap).map(([label, run]) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              onClick={() => {
                setMenu(null);
                run();
              }}
              className="w-full text-left px-3 py-2 rounded-md text-[14px] font-medium text-[#27272A] hover:bg-[#F4F4F5]"
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {dismissFor && <DismissModal gap={dismissFor} onCancel={() => setDismissFor(null)} onDismiss={(r) => dismiss(dismissFor, r)} />}

      <div className="flex flex-col gap-[10px] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            title="Back to All Files"
            className="w-[30px] h-[30px] flex items-center justify-center border border-[#A1A1AA] rounded-md bg-white hover:border-[#A04A97]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#52525B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span onClick={onBack} className="text-[15px] font-medium text-[#52525B] cursor-pointer hover:text-[#A04A97]">
            All Files
          </span>
          <svg width="6" height="9" viewBox="0 0 6 9" fill="#52525B">
            <path d="M3.37 4.5.22 1.35a.79.79 0 0 1 1.13-1.13l3.71 3.71a.8.8 0 0 1 0 1.13L1.35 8.78A.79.79 0 0 1 .22 7.65L3.37 4.5z" />
          </svg>
          <span className="text-[15px] font-semibold text-[#18181B]">Knowledge Gaps</span>
        </div>

        <div className="flex items-end justify-between gap-4 border-b border-[#D4D4D8] flex-wrap">
          <div className="flex items-end gap-1 -mb-px">
            {STATUS_TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-3 pt-2 pb-[10px] border-b-2 text-[15px] whitespace-nowrap ${
                  tab === key ? "border-[#A04A97] font-semibold text-[#A04A97]" : "border-transparent font-medium text-[#52525B] hover:text-[#18181B]"
                }`}
              >
                {label}
                {tabCount(key) != null && (
                  <span className="px-[7px] rounded-full bg-[#F7E9F5] text-[12px] font-semibold leading-[18px] text-[#6E2A67]">
                    {tabCount(key)}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pb-2">
            <span className="text-[13px] font-medium text-[#3F3F46] whitespace-nowrap">Asked in</span>
            <div className="flex p-[2px] border border-[#A1A1AA] rounded-md bg-white">
              {CONTEXTS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setContext(value)}
                  className={`px-3 py-1 rounded text-[13px] font-medium whitespace-nowrap ${
                    context === value ? "bg-[#A04A97] text-white" : "text-[#27272A] hover:bg-[#F4F4F5]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="m-0 text-[14px] leading-[1.5] text-[#3F3F46]">
          Questions your team asked in chat that Sensa couldn't answer from the Knowledge Base. Adding a document that answers
          one closes it.
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pt-3 pb-2">
        {items === null && (
          <div className="flex items-center gap-2 py-10 justify-center text-[14px] text-[#71717A]">
            <Spinner color="#A04A97" /> Loading knowledge gaps...
          </div>
        )}
        {items !== null && error && <p className="m-0 py-10 text-center text-[14px] text-[#B42318]">{error}</p>}
        {items !== null && !error && items.length === 0 && (
          <div className="py-16 text-center">
            <p className="m-0 mb-1.5 text-[17px] font-semibold text-[#18181B]">
              {tab === "open" ? "No open gaps" : tab === "closed" ? "No closed gaps yet" : "No dismissed gaps"}
            </p>
            <p className="m-0 text-[14px] text-[#3F3F46]">
              {tab === "open"
                ? "When chat can't answer a question from the Knowledge Base, it shows up here."
                : tab === "closed"
                ? "Gaps answered by a document you added show up here."
                : "Gaps you dismiss show up here."}
            </p>
          </div>
        )}
        {items !== null && !error && items.length > 0 && (
          <div ref={tableRef} className="border border-[#D4D4D8] rounded-lg overflow-x-auto">
            <div className={compact ? "" : isOpenTab ? "min-w-[790px]" : "min-w-[720px]"}>
              <div className={`${grid} h-10 bg-[#F4F4F5] border-b border-[#A1A1AA] text-[13px] font-semibold text-[#27272A]`}>
                <span className={compact ? "px-3" : "px-4"}>{isOpenTab ? "Unanswered question" : "Question"}</span>
                {!compact && <span className={pad}>Asked in</span>}
                {isOpenTab ? (
                  !compact && (
                    <>
                      <span className={pad}>Asked by</span>
                      <span className={pad}>Date</span>
                    </>
                  )
                ) : (
                  <span className={pad}>{tab === "closed" ? "Answered by" : "Dismissed by"}</span>
                )}
                {!isOpenTab && !compact && <span className={pad}>{tab === "closed" ? "Closed on" : "Dismissed on"}</span>}
                {isOpenTab && <span />}
              </div>
              {items.map((gap) => {
                const busy = busyId === gap.id;
                // Second line of the Answered by / Dismissed by cell; compact rows carry the date here too.
                const detail = [
                  tab === "closed" ? gap.resolved_by && `by ${gap.resolved_by}` : gap.dismiss_reason,
                  compact && gap.resolved_at ? formatDate(gap.resolved_at) : null,
                ].filter(Boolean).join(" · ");
                return (
                  <div
                    key={gap.id}
                    className={`${grid} min-h-[64px] border-b border-[#D4D4D8] last:border-b-0 hover:bg-[#F8F3FF] ${busy ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <div className={`min-w-0 ${compact ? "px-3" : "px-4"} py-2.5 flex flex-col gap-[3px]`}>
                      <span className="text-[14px] font-medium leading-snug text-[#18181B] break-words">
                        {gap.question}
                        {gap.times_asked > 1 && (
                          <span className="ml-2 inline-flex px-2 rounded-full bg-[#F4F4F5] text-[12px] font-semibold leading-[18px] text-[#3F3F46] align-middle whitespace-nowrap">
                            Asked {gap.times_asked} times
                          </span>
                        )}
                      </span>
                      {compact && (
                        <span className="truncate text-[12px] text-[#71717A]">
                          {[gap.context, gap.project?.name, gap.asked_by, formatDate(gap.asked_at)].filter(Boolean).join(" · ")}
                        </span>
                      )}
                      {gap.reply && (
                        <span title={gap.reply} className="truncate text-[13px] text-[#3F3F46]">
                          Sensa replied: “{gap.reply}”
                        </span>
                      )}
                      {gap.status === "open" && ["failed", "error"].includes(gap.document?.status) && (
                        <span title={gap.document.name} className="truncate text-[12px] text-[#B42318]">
                          Last file failed to process: {gap.document.name}
                        </span>
                      )}
                    </div>
                    {!compact && (
                      <div className={`min-w-0 ${pad} flex flex-col items-start gap-1`}>
                        <span className={`${PILL} border-[#A1A1AA] font-medium text-[#27272A]`}>{gap.context}</span>
                        {gap.project && (
                          <span title={gap.project.name} className="max-w-full truncate text-[12px] text-[#71717A]">
                            {gap.project.name}
                          </span>
                        )}
                      </div>
                    )}
                    {isOpenTab ? (
                      <>
                        {!compact && (
                          <>
                            <span title={gap.asked_by} className={`min-w-0 ${pad} truncate text-[14px] text-[#27272A]`}>
                              {gap.asked_by}
                            </span>
                            <span className={`${pad} text-[14px] text-[#27272A] whitespace-nowrap`}>{formatDate(gap.asked_at)}</span>
                          </>
                        )}
                        <div className={`${compact ? "pl-2 pr-3" : "pl-3 pr-4"} flex items-center justify-end gap-2`}>
                          {busy && <Spinner size={14} color="#A04A97" />}
                          {gap.status === "pending" ? (
                            <span
                              title={`Source: ${gap.document?.name || "document"}. Approve it with View & Ingest and this gap closes.`}
                              className={`${PILL} border-[#D9B53A] bg-[#FFF6D6] font-semibold text-[#7A5B00]`}
                            >
                              Waiting for ingest
                            </span>
                          ) : (
                            !compact && (
                              <button
                                type="button"
                                onClick={() => pickSource(gap)}
                                className="px-3 py-[5px] border border-[#A04A97] rounded-md bg-white text-[13px] font-semibold text-[#A04A97] hover:bg-[#FDF2F8] whitespace-nowrap"
                              >
                                Add a source
                              </button>
                            )
                          )}
                          <button
                            type="button"
                            onClick={(e) => openMenu(e, gap)}
                            title="More actions"
                            aria-label="More actions"
                            aria-expanded={menu?.gap.id === gap.id}
                            className="w-[30px] h-[30px] flex-shrink-0 flex items-center justify-center border border-[#A1A1AA] rounded-md bg-white text-[#52525B] hover:bg-[#F4F4F5]"
                          >
                            <ThreeDotsIcon />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`min-w-0 ${compact ? "pl-2 pr-3" : pad} flex flex-col gap-[2px]`}>
                          {tab === "closed" ? (
                            gap.document ? (
                              <button
                                type="button"
                                title={gap.document.name}
                                onClick={() => onOpenDocument?.(gap.document)}
                                className="min-w-0 max-w-full truncate text-left text-[14px] font-medium text-[#A04A97] hover:underline"
                              >
                                {gap.document.name}
                              </button>
                            ) : (
                              <span className="text-[14px] text-[#71717A]">
                                {gap.closed_without_document ? "Answered in chat" : "Document deleted"}
                              </span>
                            )
                          ) : (
                            <span title={gap.resolved_by || ""} className="truncate text-[14px] text-[#27272A]">
                              {gap.resolved_by || "Former member"}
                            </span>
                          )}
                          {detail && <span title={detail} className="truncate text-[12px] text-[#71717A]">{detail}</span>}
                        </div>
                        {!compact && (
                          <span className={`${pad} text-[14px] text-[#27272A] whitespace-nowrap`}>
                            {formatDate(gap.resolved_at || gap.asked_at)}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeGapsPage;
